import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import _ from "lodash";
import cx from "classnames";
import styles from "./preview.module.scss";
import { useSelector } from "react-redux";
import {
  fetchCallMeeting,
  invalidateCallMeeting,
} from "src/redux/actions/coaching/callLibrary";
import userRoleService from "src/utils/userRoles.service";

import { CourseNotFound } from "../../components/notFound";
import PermissionDenined from "src/components/permissionDenied";
import { LoadingCoursePage } from "../../components/course/view";
import { CallMeetingContext, CallMeetingContextProps } from "../context";
import { TabBar } from "src/components/tabbar";
import { SimulationParameterEvaluation } from "src/types/coaching/learners";
import { Nav, Tab } from "react-bootstrap";
import { animated, useSpring, useTransition } from "@react-spring/web";

import CallRecordingView from "./recording";
import { CallMeeting } from "src/types/coaching/callLibrary";
import pluralize from "pluralize";

const PreviewMeetingPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const params = useParams();
  const id = params.id;

  const loading = useSelector((state: any) => {
    return _.get(state.coaching.callMeetings, `meeting[${id}].loading`);
  });

  const meeting = useSelector((state: any) => {
    return _.get(
      state.coaching.callMeetings,
      `meeting[${id}].item`,
    ) as CallMeeting;
  });

  const meetingErrorCode = useSelector((state: any) => {
    return _.get(
      state.coaching.callMeetings,
      `meetings[${id}].error.errors[0].code`,
    );
  });

  const [meetingContext, setMeetingContext] = useState<CallMeetingContextProps>(
    {
      id: id,
    },
  );

  //use effects
  useEffect(() => {
    if (id) {
      dispatch(fetchCallMeeting({ id }));
    }

    return () => {
      if (id) {
        dispatch(invalidateCallMeeting());
      }
    };
  }, [id]);

  if (meetingErrorCode === 10007) {
    return (
      <CourseNotFound
        onBack={() => {
          navigate(`/coaching/meetings`);
        }}
      />
    );
  }

  if ((loading && meeting?.id != id) || !meeting) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.backContainer}>
            <div
              className={styles.backButton}
              onClick={() => {
                navigate(`/coaching/meetings`);
              }}
            >
              <i className="paperflite-left-arrow" ></i>
              <span className={styles.back}>back to <span className={styles.bold}>"call library"</span></span>{" "}
            </div>
          </div>
        </div>
        <div className={styles.editContainer}>
          <div className={styles.left}>
            <LoadingMeetingSummary />
          </div>
          <div className={styles.right}></div>
        </div>
      </div>
    );
  }

  return (
    <CallMeetingContext.Provider value={meetingContext}>
      <MeetingSummaryComponent meeting={meeting} />
    </CallMeetingContext.Provider>
  );
};

const MeetingSummaryComponent = ({ meeting }: { meeting: CallMeeting }) => {
  console.log("meeting,",meeting)
  const navigate = useNavigate();
  
  const uploadedBy = useMemo(() => {
    return meeting.uploadedBy || "";
  }, [meeting]);

  const title = useMemo(() => {
    return meeting.title || "";
  }, [meeting]);

  const summary = useMemo(() => {
    return meeting.summary;
  }, [meeting]);

  const duration = useMemo(() => {
    return meeting.duration || "0";
  }, [meeting]);

  const prospect = useMemo(() => {
    return meeting.prospect || "";
  }, [meeting]);

  const salesRep = useMemo(() => {
    return meeting.salesRep || "";
  }, [meeting]);

  const modifiedDate = useMemo(() => {
    return meeting.modifiedDate;
  }, [meeting]);

  const score = useMemo(() => {
    return meeting.score || 0;
  }, [meeting]);

  const percentage = Math.floor((score as number) / 5 * 100).toFixed(0) || 0

  const dateMap = new Date(modifiedDate ?? 0);
  const date = dateMap.getDate();
  const day = dateMap.toLocaleString("default", { weekday: "short" });
  const month = dateMap.toLocaleString("default", { month: "short" });

  const [activeTab, setActiveTab] = useState(0);

  const onBackClick = () => {
    navigate("/coaching/meetings");
  };

  return (
    <div key={meeting.id} className={styles.container}>
      <div className={styles.preview}>
        <div className={styles.left}>
          <div className={styles.header}>
            <div className={styles.backContainer}>
              <div className={styles.backButton} onClick={onBackClick}>
                <i className="paperflite-left-arrow"></i>
                <span className={styles.back}>
                  back to <span className={styles.bold}>"call library"</span>
                </span>{" "}
              </div>
            </div>
          </div>

          <div className={styles.meetingDetails}>
            <div className={styles.detailRow}>
              <div className={styles.dateBadge}>
                <span className={styles.month}>{month}</span>
                <span className={styles.day}>{date}</span>
              </div>

              <div className={styles.details}>
                <span className={styles.title}>{title}</span>

                <div className={styles.metadatas}>
                  <i className={cx("paperflite-clock", styles.duration)} />
                  <span className={styles.name}>
                    {duration} {pluralize("minute", Number(duration))}{" "}
                  </span>
                </div>
              </div>
            </div>

            <div className={styles.information}>
              {prospect && (
              <div className={styles.item}>
                <Avatar name={prospect} />
                <span className={styles.name}>Prospect: {prospect}</span>
              </div>
              )}

              {uploadedBy && (
                <div className={styles.item}>
                  <Avatar name={uploadedBy} />
                  <span className={styles.name}>Uploaded by: {uploadedBy}</span>
                </div>
              )}

              {salesRep && (
              <div className={styles.item}>
                <Avatar name={salesRep} small />
                <span className={styles.name}>Sales Rep: {salesRep}</span>
              </div>
              )}
            </div>
          </div>

          <TabBar
            className={cx(styles.tabs)}
            gap={32}
            tabSize="equal"
            active={activeTab}
            onSelectionChange={(index) => setActiveTab(index)}
          >
            <span
              className={cx(styles.tab, {
                [styles.active]: activeTab === 0,
              })}
            >
              summary
            </span>
            <span
              className={cx(styles.tab, {
                [styles.active]: activeTab === 1,
              })}
            >
              assessment
            </span>

            <span
              className={cx(styles.tab, {
                [styles.active]: activeTab === 2,
              })}
            >
              recording
            </span>
          </TabBar>

          <TabSection index={0} selected={activeTab}>
            <MeetingSummaryVew summary={summary} />
          </TabSection>

          <TabSection index={1} selected={activeTab}>
            <CallAssesmentScoreView meeting={meeting} />
          </TabSection>

          <TabSection index={2} selected={activeTab}>
            <CallRecordingView meeting={meeting} />
          </TabSection>
        </div>
        <div className={styles.dividerLine}></div>

        <div className={styles.right}>
          <div className={styles.infoCard}>
            <div className={styles.metrics}>
              <span className={styles.name}>score</span>
              <span className={styles.value}>{percentage}%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewMeetingPage;

const MeetingSummaryVew = ({ summary }) => {
  const salesRepPositives = _.get(summary, "salesRepPositives", []);
  const prospectObservations = _.get(summary, "prospectObservations", []);
  const suggestions = _.get(summary, "suggestions", []);

  return (
    <div className={styles.summaryContainer}>
      {salesRepPositives.length > 0 && (
        <div className={styles.positives}>
          <span className={styles.label}>Positives</span>
          <ul className={styles.items}>
            {salesRepPositives.map((item, i) => (
              <li key={i} className={styles.item}>
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}

      {prospectObservations.length > 0 && (
        <div className={styles.observations}>
          <span className={styles.label}>Observations</span>
          <ul className={styles.items}>
            {prospectObservations.map((item, i) => (
              <li key={i} className={styles.item}>
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}

      {suggestions.length > 0 && (
        <div className={styles.suggestions}>
          <span className={styles.label}>Suggestions</span>
          <ul className={styles.items}>
            {suggestions.map((item, i) => (
              <li key={i} className={styles.item}>
                {item}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const Avatar = ({ name, small = false }) => {
  const initials = (name = "") => {
    return name.slice(0, 2).toUpperCase();
  };
  return (
    <span className={cx(styles.avatar, { [styles.avatarSmall]: small })}>
      {initials(name)}
    </span>
  );
};

const CallAssesmentScoreView = ({ meeting }) => {
  const [activeTab, setActiveTab] = useState(0);

  const parameters = useMemo(() => {
    return meeting.evaluations || [];
  }, [meeting]);

  return (
    <div className={styles.callAssessmentContainer}>
      <TabBar
        className={cx(styles.tabs)}
        gap={32}
        tabSize="equal"
        active={activeTab}
        onSelectionChange={(index) => setActiveTab(index)}
      >
        {parameters.map((parameter, index) => (
          <span
            key={parameter.uuid}
            className={cx(styles.tab, {
              [styles.active]: activeTab === index,
            })}
          >
            <div className={styles.name}>
              {parameter.name} ({Math.round(parameter.score)}/5)
            </div>
          </span>
        ))}
      </TabBar>
      <div className={styles.callAssessmentParamater}>
        {parameters.map(
          (parameter, index) =>
            activeTab === index && (
              <div key={parameter.uuid}>
                <CallAssessmentParameterView parameter={parameter} />
              </div>
            ),
        )}
      </div>
    </div>
  );
};

interface CallAssessmentParameterViewProps {
  parameter: SimulationParameterEvaluation;
}

const CallAssessmentParameterView: React.FC<
  CallAssessmentParameterViewProps
> = ({ parameter }) => {
  return (
    <div className={styles.callAssessmentParameterContainer}>
      <div className={styles.parameterTabContainer}>
        <Tab.Container defaultActiveKey="0">
          <Nav className={styles.tabs}>
            {parameter.parameters.map((parameter, index) => {
              return (
                <Nav.Link
                  key={parameter.uuid}
                  className={styles.tab}
                  eventKey={index}
                >
                  <span>
                    {parameter.name} ({parameter.score})
                  </span>
                </Nav.Link>
              );
            })}
          </Nav>
          <Tab.Content className={styles.tabContent}>
            {parameter.parameters.map((subParameter, index) => {
              const showEmptyMessage =
                _.isEmpty(subParameter.scenarios) &&
                _.isEmpty(subParameter.positives) &&
                _.isEmpty(subParameter.suggestions) &&
                _.isEmpty(subParameter.missedOpportunities) &&
                (!subParameter.score || subParameter.score === 0);

              if (showEmptyMessage) {
                return (
                  <Tab.Pane
                    className={styles.parameterContainer}
                    eventKey={index}
                    key={subParameter.uuid}
                  >
                    <div className={styles.noData}>
                      <div className={styles.text}>No scenario found</div>
                      <span className={styles.subText}>
                        There were no relevant scenarios in the call to evaluate
                        this parameter.
                      </span>
                    </div>
                  </Tab.Pane>
                );
              }

              return (
                <Tab.Pane
                  className={styles.parameterContainer}
                  eventKey={index}
                  key={subParameter.uuid}
                >
                  <div className={styles.parameter}>
                    <div className={styles.label}>Description</div>
                    <div className={styles.value}>
                      {subParameter.description}
                    </div>
                  </div>
                  <div className={styles.parameter}>
                    <div className={styles.label}>Score</div>
                    <div className={styles.value}>{subParameter.score}</div>
                  </div>
                  {_.size(subParameter.scenarios) > 0 && (
                    <div className={styles.parameter}>
                      <div className={styles.label}>Scenario</div>
                      <div className={styles.value}>
                        <ul className={styles.content}>
                          {_.map(subParameter.scenarios, (scenario, index) => (
                            <li key={index}>
                              <p>{scenario.description}</p>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {_.size(subParameter.positives) > 0 && (
                    <div className={styles.parameter}>
                      <div className={styles.label}>Positives</div>
                      <div className={styles.value}>
                        {subParameter.positives}
                      </div>
                    </div>
                  )}

                  <div className={styles.parameter}>
                    <div className={styles.label}>Suggestions</div>
                    <div className={styles.value}>
                      {subParameter.suggestions}
                    </div>
                  </div>

                  {_.size(subParameter.missedOpportunities) > 0 && (
                    <div className={styles.parameter}>
                      <div className={styles.label}>Missed opportunities</div>
                      <div className={styles.value}>
                        <ul className={styles.content}>
                          {_.map(
                            subParameter.missedOpportunities,
                            (item, index) => (
                              <li key={index}>
                                <p className={styles.example}>{item}</p>
                              </li>
                            ),
                          )}
                        </ul>
                      </div>
                    </div>
                  )}
                </Tab.Pane>
              );
            })}
          </Tab.Content>
        </Tab.Container>
      </div>
    </div>
  );
};

interface TabSectionProps {
  selected: number;
  index: number;
  children: React.ReactNode;
}

export const TabSection: React.FC<TabSectionProps> = ({
  index,
  selected,
  children,
}) => {
  const active = index === selected;
  const [animation] = useSpring(
    {
      height: active ? "auto" : 0,
      opacity: active ? 1 : 0,
      overflow: "hidden",
      transform: active
        ? `translate3d(0,0,0)`
        : `translate3d(${100 * (index > selected ? 1 : -1)}%, 0, 0)`,
    },
    [active],
  );

  return (
    <animated.div
      style={animation}
      className={cx(styles.tabBody, styles.stack)}
    >
      {children}
    </animated.div>
  );
};

const LoadingMeetingSummary =() => {
  return(
    <div className={cx(styles.loadingContainer, styles.placeholder)}>
      <div className={cx(styles.left)} >
        <div className={cx(styles.first)}>
          <div className={cx(styles.date, styles.block)}></div>
          <div className={cx(styles.nameWrapper)}>
            <div className={cx(styles.name, styles.block)}></div>
            <div className={cx(styles.duration, styles.block)}></div>
          </div>
        </div>
        <div className={cx(styles.metadata)}>
          {[...Array(3)].map((_, index) => (
            <div key={index} className={cx(styles.pill, styles.block)}>
            </div>
          ))}
        </div>
        <div className={cx(styles.bottom)}>
          <div className={styles.pills}>
            {[...Array(3)].map((_, index) => (
              <div className={cx(styles.pill, styles.block)}></div>
            ))}
          </div>
          <div className={styles.description}>
            <div className={cx(styles.lineone, styles.block)}></div>
            <div className={cx(styles.linetwo, styles.block)}></div>
            <div className={cx(styles.linethree, styles.block)}></div>
          </div>
        </div>
      </div>
      <div className={cx(styles.divider)}>
      </div>
      <div className={cx(styles.right)}>
        <div className={styles.top}>
          <div className={cx(styles.box, styles.block)}></div>
          {/* <div className={cx(styles.box, styles.block)}></div> */}
        </div>
        <div className={styles.bottom}>
          <div className={cx(styles.header,styles.block)}></div>
          <div className={styles.cardWrapper}>
            <div className={cx(styles.date, styles.block)}>
            </div>
            <div className={styles.linewrapper}>
              <div className={cx(styles.linebig, styles.block)}></div>
              <div className={cx(styles.linesmall, styles.block)}></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}